import {
  Col,
  Collapse,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Spin,
} from "antd-v5";
import message from "components/CustomMessage";
import MessageEditor from "components/MessageEditor";
import Vocabulary, { getVocabulary } from "components/Vocabulary";
import { useHasDisabledEnvironment } from "contexts/GlobalSettingsContext";
import dayjs from "dayjs";
import { isNull } from "lodash";
import { marked } from "marked";
import moment from "moment";
import React, { useCallback, useEffect, useRef, useState } from "react";
import AssessmentAPI from "restAPI/AssessmentAPI";
import GlobalSettingAPI from "restAPI/GlobalSettingAPI";
import ProductAPI from "restAPI/ProductAPI";
import SubProductAPI from "restAPI/SubProductAPI";
import UserAPI from "restAPI/UserAPI";
import { NameIdPair } from "types/api/nameIdPair";
import { UserInfoProjectorImpl } from "types/api/userInfoProjectorImpl";
import {
  AssessmentCompositeRequestDto,
  AssessmentResponseDto,
  AssessmentStatusEnum,
} from "types/assessment";
import {
  filterVocabularyOption,
  nameRegexValidator,
  parseSelectOptions,
} from "utils/formUtils";
import htmlToMarkDown, { sanitizeMarkdown } from "utils/htmlToMarkDownUtil";
import _, { isArray } from "utils/lodashUtils";
import { Environments, RESERVED_TAG_KEYS } from "views/Findings/Constants";

import LoadingIcon from "ac-components/ac-icons/LoadingIcon";
import ACModal from "ac-components/ac-modal/ACModal";
import { ReportSectionState } from "ac-components/ac-report-editor/constatnts";
import ACTagTable from "ac-components/ac-tag-table/ACTagTable";

export type AssessmentPayloadData = Omit<
  AssessmentCompositeRequestDto,
  "scope" | "startDate" | "endDate" | "tagList"
> & {
  productId: number;
  subProductIds: number;
  startDate: dayjs.Dayjs;
  endDate: dayjs.Dayjs;
  environmentName: string;
  tagList: Array<{ key: string; value: string }>;
};

interface AssessmentsFormProps {
  open: boolean;
  onOk: () => void;
  onCancel: () => void;
  onSuccess: (data?: AssessmentResponseDto) => void;
  editID?: number;
  cloneID?: number;
  initialValues?: Partial<AssessmentPayloadData>;
}

export type User = {
  value: string | number;
  label: string;
};

const AssessmentsFormPopup = ({
  open,
  onOk,
  onCancel,
  onSuccess,
  editID,
  cloneID,
  initialValues,
}: AssessmentsFormProps) => {
  const [form] = Form.useForm<AssessmentPayloadData>();
  const [products, setProducts] = useState<Array<NameIdPair> | undefined>([]);
  const [subProducts, setSubProducts] = useState<
    Array<NameIdPair> | undefined | null
  >(undefined);
  const [users, setUsers] = useState<Array<User>>();
  const [assessmentType, setAssessmentType] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const reportToClone = useRef<ReportSectionState[]>();
  const displayText = editID ? "Edit" : cloneID ? "Duplicate" : "Create";
  const successMessage = editID ? "Edited" : cloneID ? "Duplicated" : "Created";
  const { hasDisabledEnvironment, envValue } = useHasDisabledEnvironment();
  const [tags, setTags] = useState<{ key: string; value: string }[]>([]);

  const fetchSubProducts = useCallback((productId: number) => {
    setSubProducts(null);
    const { promise, cancel } = SubProductAPI.getAllQuick(productId);
    promise.then(setSubProducts, _.noop);
    return cancel;
  }, []);

  const onProductSelect = useCallback(
    (productId: number) => {
      setSubProducts([]);
      form.resetFields(["subProductIds"]);
      if (productId) {
        fetchSubProducts(productId);
      }
    },
    [fetchSubProducts, form],
  );

  useEffect(() => {
    if (initialValues?.productId) {
      fetchSubProducts(initialValues.productId);
    }
  }, [initialValues?.productId]);

  useEffect(() => {
    const fetchID = editID ? editID : cloneID;
    if (fetchID) {
      setLoading(true);
      const { promise, cancel } = AssessmentAPI.get(fetchID);
      let cancelReport: () => void;
      promise.then((response) => {
        const formData = convertResponseToFormData(response);
        setTags(formData.tagList || []);
        fetchSubProducts(formData.productId);
        form.setFieldsValue(formData);

        if (cloneID) {
          const { promise, cancel } = AssessmentAPI.getReport(cloneID);
          cancelReport = cancel;
          promise
            .then((res) => {
              try {
                if (!_.isEmpty(res?.report)) {
                  const parsedReport = JSON.parse(res.report);
                  reportToClone.current = parsedReport;
                }
              } catch (e) {
                console.error("report clone fail", e);
              }
            })
            .finally(() => setLoading(false));
        } else {
          setLoading(false);
        }
      });
      return () => {
        cancel();
        cancelReport && cancelReport();
      };
    }
  }, [editID, fetchSubProducts, form, cloneID]);

  useEffect(() => {
    const { promise, cancel } = UserAPI.getAll();
    const { promise: promise2, cancel: cancel2 } = ProductAPI.getAllQuick();
    const { promise: promise3, cancel: cancel3 } =
      GlobalSettingAPI.getAssessmentConfig();
    Promise.all([promise, promise2, promise3]).then(
      ([userData, prodData, data]) => {
        setUsers(
          userData.map((d: UserInfoProjectorImpl) => ({
            value: d.id || "",
            label: d.displayName || "",
          })),
        );
        setProducts(parseSelectOptions(prodData));
        setAssessmentType(data.types);
      },
    );

    return () => {
      cancel();
      cancel2();
      cancel3();
    };
  }, []);

  const onSubmit = useCallback(async () => {
    try {
      await form.validateFields();
      setLoading(true);
      const {
        startDate,
        endDate,
        type,
        productId,
        subProductIds,
        environmentName,
        notes,
        ...data
      } = form.getFieldsValue();
      let _environment = environmentName;
      if (hasDisabledEnvironment) {
        _environment = envValue;
      }
      const requestNotes: any = notes;
      const htmlNotes: string = notes
        ? sanitizeMarkdown(htmlToMarkDown(requestNotes)).toString()
        : "";
      const assessmentPayloadData: AssessmentCompositeRequestDto = {
        ...data,
        notes: htmlNotes,
        startDate: moment(dayjs(startDate).toISOString()).utc().format(),
        endDate: moment(dayjs(endDate).toISOString()).utc().format(),
        type,
        status: AssessmentStatusEnum["INPROGRESS"],
        tagList: _.map(
          data.tagList,
          (entry) => `${entry.key}${entry.value ? `${`:${entry.value}`}` : ""}`,
        ),
        scope: {
          productId,
          subProductIds: isArray(subProductIds)
            ? subProductIds
            : [subProductIds],
          environmentName: _environment,
        },
      };

      if (editID) {
        await AssessmentAPI.updateAssessment(editID, assessmentPayloadData)
          .promise;
        onSuccess();
      } else {
        const data = await AssessmentAPI.createAssessment(assessmentPayloadData)
          .promise;
        if (cloneID && !_.isEmpty(reportToClone.current)) {
          await AssessmentAPI.createReport({
            assessmentId: data.id,
            report: JSON.stringify(
              _.map(reportToClone.current, (entry) =>
                _.omit(entry, ["id", "index", "placeholder"]),
              ),
            ),
          }).promise;
        }
        onSuccess(data);
      }
      onOk();
      message.success(
        `Successfully ${successMessage} ${getVocabulary("assessment")}`,
      );
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  }, [
    form,
    hasDisabledEnvironment,
    editID,
    onSuccess,
    onOk,
    successMessage,
    envValue,
    cloneID,
  ]);

  return (
    <>
      <ACModal
        title={`${displayText} ${getVocabulary("assessment")}`}
        open={open}
        onOk={onSubmit}
        okText={`${displayText} ${getVocabulary("assessment")}`}
        onCancel={onCancel}
        width={696}
        destroyOnClose
        style={{ top: 40 }}
        okButtonLoading={loading}
      >
        <Spin spinning={loading}>
          <Form
            form={form}
            layout="vertical"
            preserve={false}
            initialValues={initialValues}
          >
            <Col>
              <Form.Item
                name="name"
                label={
                  <>
                    <Vocabulary name="assessment" /> Name
                  </>
                }
                rules={[
                  { required: true },
                  //@ts-ignore
                  nameRegexValidator({ regex: "", max: 100 }),
                ]}
              >
                <Input
                  placeholder={`${getVocabulary("assessment")} Name`}
                  autoFocus
                />
              </Form.Item>
            </Col>
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <Form.Item
                  name="type"
                  label="Type"
                  rules={[{ required: true }]}
                >
                  <Select
                    showSearch
                    options={parseSelectOptions(assessmentType)}
                    placeholder="Type"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <Form.Item
                  name="productId"
                  label="Scope"
                  rules={[
                    {
                      required: true,
                      message: (
                        <>
                          Please enter <Vocabulary name="product" />
                        </>
                      ),
                    },
                  ]}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    placeholder={<Vocabulary name="product" isPlural />}
                    options={products}
                    onChange={onProductSelect}
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="subProductIds"
                  label=" "
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value) {
                          return Promise.reject(
                            <>
                              Please enter <Vocabulary name="subProduct" />
                            </>,
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Select
                    showSearch
                    placeholder={<Vocabulary name="subProduct" isPlural />}
                    options={parseSelectOptions(subProducts)}
                    optionFilterProp="label"
                    allowClear
                    notFoundContent={
                      isNull(subProducts) ? (
                        <div className="text-center">
                          <LoadingIcon />
                        </div>
                      ) : !form.getFieldValue("productId") ? (
                        <>
                          Select a <Vocabulary name="product" />
                        </>
                      ) : undefined
                    }
                  />
                </Form.Item>
              </Col>
              {!hasDisabledEnvironment && (
                <Col span={8}>
                  <Form.Item
                    name="environmentName"
                    label=" "
                    rules={[
                      {
                        validator: (_, value) => {
                          if (!value) {
                            return Promise.reject(
                              <>Please enter Environment</>,
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Select
                      showSearch
                      placeholder={"Environment"}
                      options={parseSelectOptions(Environments)}
                      filterOption={filterVocabularyOption}
                      allowClear
                    />
                  </Form.Item>
                </Col>
              )}
            </Row>
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <Form.Item
                  name="startDate"
                  label={"Start Date"}
                  rules={[{ required: true }]}
                >
                  <DatePicker style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="endDate"
                  label={"End Date"}
                  rules={[
                    {
                      validator: (_, value) => {
                        const startDate = form.getFieldValue("startDate");
                        const endDateMoment = dayjs(value);
                        const startDateMoment = dayjs(startDate);
                        if (
                          value &&
                          endDateMoment.isSame(startDateMoment, "day")
                        ) {
                          return Promise.reject(
                            "The end date cannot be same as start date. Please choose a valid end date.",
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                    { required: true },
                  ]}
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    disabledDate={(current) =>
                      current &&
                      form.getFieldValue("startDate") &&
                      current.isBefore(form.getFieldValue("startDate"), "day")
                    }
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <Form.Item
                  name="requestedBy"
                  label="Requestor"
                  rules={[{ required: true }]}
                >
                  <Select
                    showSearch
                    placeholder={"Requestor"}
                    options={users}
                    optionFilterProp="label"
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col span={16}>
                <Form.Item
                  name="assessors"
                  label="Assessors"
                  rules={[{ required: true }]}
                >
                  <Select
                    placeholder={"Assessors"}
                    options={users}
                    mode="multiple"
                    showSearch
                    optionFilterProp="label"
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>

            <Collapse className="m-b-sm">
              <Collapse.Panel header="Tags" key="1" forceRender>
                <Form.Item name="tagList">
                  {/** @ts-expect-error */}
                  <ACTagTable
                    editable
                    source={tags}
                    preventKeys={RESERVED_TAG_KEYS}
                    permission={
                      editID ? "UPDATE_ASSESSMENTS" : "CREATE_ASSESSMENTS"
                    }
                    noStyle={true}
                    prefKey="assessment-form-tag-table"
                  />
                </Form.Item>
              </Collapse.Panel>
            </Collapse>
            <Form.Item name="notes" label="Request Notes">
              {!loading && (
                <MessageEditor
                  placeholder="Request Notes"
                  toolBarIdentifier="message-editor-request-notes"
                  hideEmoji
                  hideOldUpload
                  alwaysOpen
                  showFooterButtons={false}
                />
              )}
            </Form.Item>
            {/* <Collapse
            items={[{ key: "1", label: "Advanced Details" }]}
            bordered={false}
            ghost
          /> */}
          </Form>
        </Spin>
      </ACModal>
    </>
  );
};

export default AssessmentsFormPopup;

const convertResponseToFormData = (response: AssessmentResponseDto) => {
  const {
    name,
    type,
    startDate,
    endDate,
    notes,
    assessors,
    requestedBy,
    status,
    scope,
    tagList,
  } = response;
  const formData: AssessmentPayloadData = {
    name,
    type,
    notes: notes ? (marked.parse(notes) as string) : "",
    assessors,
    requestedBy,
    status,
    startDate: dayjs(startDate),
    endDate: dayjs(endDate),
    productId: scope.product,
    subProductIds: scope.subProductIds[0],
    environmentName: scope.environmentName,
    tagList: _.map(tagList, (entry) => {
      const [key, value] = entry.split(":");
      return { key: key, value: value };
    }),
  };
  return formData;
};
